import { useParams } from "react-router";
import t from "@/lang/lang";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { WorkOrderPdf } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/work-order-pdf";
import {
  OfflineState,
  useOfflineContext,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/offline-context";
import { OfflineButton } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/offline-button";
import { useGetWorkOrder } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-work-order-offline";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { SignatureButton } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/signature-button";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { useErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { hasFieldError } from "@/shared/lib/errors/validation-error";
import { WorkOrderProviders } from "@/routes/processes/[processId]/htz/work-orders/_components/work-order-providers";
import { useWorkOrderStateGuard } from "@/routes/processes/[processId]/htz/work-orders/_components/use-work-order-state-guard";
import { FulfillmentState } from "@/services/backend/relationships/types/fulfillment";
import { FulfillmentStateBadge } from "@/services/backend/relationships/components/fulfillment-state-badge";
import { Separator } from "@/shared/components/ui/separator";
import { BackToPlanningDialog } from "@/services/backend/htz/work-order/components/back-to-planning-dialog";
import { CompleteExecutionDialog } from "@/services/backend/htz/work-order/components/complete-execution-dialog";
import { WorkOrder } from "@/services/backend/htz/work-order/types/work-order";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { WorkOrderBreadcrumbs } from "@/routes/processes/[processId]/htz/work-orders/_components/work-order-breadcrumbs";

export function WorkOrderExecutionRoute() {
  const { workOrderId } = useParams();

  return (
    <WorkOrderProviders workOrderId={workOrderId!}>
      <WorkOrderExecutionPage workOrderId={workOrderId!} />
    </WorkOrderProviders>
  );
}

function WorkOrderExecutionPage({ workOrderId }: { workOrderId: string }) {
  const { data: workOrder, isLoading, error } = useGetWorkOrder(workOrderId);

  useWorkOrderStateGuard(workOrder, FulfillmentState.Execution);

  const breadcrumbs = workOrder && (
    <WorkOrderBreadcrumbs workOrder={workOrder} segment="execution">
      {t("Ausführung")}
    </WorkOrderBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error ? <RTKQueryErrorAlert error={error} /> : null}
      {isLoading && <DocumentPageSkeleton />}
      {workOrder && <Page workOrder={workOrder} />}
    </ProcessPageMainTemplate>
  );
}

function Page({ workOrder }: { workOrder: WorkOrder }) {
  const { notReadyError } = useErrDocumentNotReady();
  const { offlineState } = useOfflineContext();

  const hasDataError = hasFieldError(
    notReadyError,
    "state",
    "executingBranchId",
    "beginDate",
    "endDate",
    "teamLeader",
    "address",
    "representative",
  );
  const hasPosError = hasFieldError(notReadyError, "position");

  const links = [
    { to: "data", hasValidationError: hasDataError, label: t("Arbeitsschein") },
    {
      to: "positions",
      hasValidationError: hasPosError,
      label: t("Leistungen"),
    },
    offlineState === OfflineState.ONLINE && {
      to: "laboratory",
      label: t("Labor"),
    },
  ].filter((link) => !!link);

  return (
    <DocumentPage>
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink
              key={link.to}
              to={link.to}
              hasValidationError={link.hasValidationError}
            >
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <Separator orientation="vertical" className="max-h-8" />
        <FulfillmentStateBadge state={workOrder.state} />
        <DocumentPageActions>
          <OfflineButton workOrderId={workOrder.id} />
          <PersistenceStateUsingContext className="mr-2" />
          <SignatureButton workOrderId={workOrder.id} />
          <Separator orientation="vertical" className="min-h-8" />
          <WorkOrderPdf workOrder={workOrder} />
          <Separator orientation="vertical" className="min-h-8" />
          <BackToPlanningDialog workOrder={workOrder} />
          <CompleteExecutionDialog workOrder={workOrder} />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
